import React from 'react';
import { Card, Toggle } from '../ui/BaseComponents';

/**
 * SettingsTab - Grouped settings panel for quiz template configuration
 * 
 * @param {Object} props
 * @param {Object} props.settings - Current settings object
 * @param {Function} props.onSettingsChange - Callback when settings change (receives full settings object)
 */
function SettingsTab({ settings = {}, onSettingsChange }) {
  const updateSetting = (key, value) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const defaultSettings = {
    displayName: settings.displayName || '',
    showIntroScreen: settings.showIntroScreen ?? true,
    anonymousMode: settings.anonymousMode ?? false,
    studentRegistration: settings.studentRegistration || 'none',  // NEW: Registration mode
    allowPractice: settings.allowPractice ?? true,  // NEW: Allow practice mode
    enableGamification: settings.enableGamification ?? true,
    basePointsPerQuestion: settings.basePointsPerQuestion ?? 100,
    enableTimeBonus: settings.enableTimeBonus ?? true,
    enableStreakBonus: settings.enableStreakBonus ?? true,
    streakBonusPoints: settings.streakBonusPoints ?? 50,
    enableQuestionTimer: settings.enableQuestionTimer ?? false,
    questionTimerSeconds: settings.questionTimerSeconds ?? 30,
    showLeaderboard: settings.showLeaderboard ?? true,
    showStreak: settings.showStreak ?? true,
    lockOnReveal: settings.lockOnReveal ?? true,
    enableJokers: settings.enableJokers ?? false,
    enableJoker5050: settings.enableJoker5050 ?? true,
    enableJokerAudience: settings.enableJokerAudience ?? true,
    enableJokerBestPlayer: settings.enableJokerBestPlayer ?? true,
    enableJokerTime: settings.enableJokerTime ?? true,
  };

  const currentSettings = { ...defaultSettings, ...settings };

  return (
    <div className="space-y-6">
      {/* APPEARANCE */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Darstellung</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Quiz-Anzeigename
            </label>
            <input
              type="text"
              value={currentSettings.displayName}
              onChange={(e) => updateSetting('displayName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="z.B. JavaScript Grundlagen Quiz"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Wird den Teilnehmern zu Beginn angezeigt
            </p>
          </div>

          <Toggle
            checked={currentSettings.showIntroScreen}
            onChange={(checked) => updateSetting('showIntroScreen', checked)}
            label="Intro-Bildschirm mit Anweisungen anzeigen"
          />

          <Toggle
            checked={currentSettings.anonymousMode}
            onChange={(checked) => updateSetting('anonymousMode', checked)}
            label="Anonymer Modus (Teilnehmer geben keine Namen ein)"
          />
        </div>
      </Card>

      {/* ANSWER MODE */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Antwortmodus
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Wie können Teilnehmer antworten?
            </label>
            
            <div className="space-y-2">
              {/* Auto Mode */}
              <label className={`
                flex items-start p-3 border-2 rounded-lg cursor-pointer theme-transition
                ${(currentSettings.multiAnswerMode || 'auto') === 'auto'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }
              `}>
                <input
                  type="radio"
                  name="multiAnswerMode"
                  value="auto"
                  checked={(currentSettings.multiAnswerMode || 'auto') === 'auto'}
                  onChange={(e) => updateSetting('multiAnswerMode', e.target.value)}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    Automatisch
                    <span className="ml-2 text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      Standard
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Mehrfachauswahl nur wenn eine Frage mehrere richtige Antworten hat
                  </div>
                </div>
              </label>

              {/* Always Multiple */}
              <label className={`
                flex items-start p-3 border-2 rounded-lg cursor-pointer theme-transition
                ${currentSettings.multiAnswerMode === 'always'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }
              `}>
                <input
                  type="radio"
                  name="multiAnswerMode"
                  value="always"
                  checked={currentSettings.multiAnswerMode === 'always'}
                  onChange={(e) => updateSetting('multiAnswerMode', e.target.value)}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    Immer Mehrfachauswahl
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Teilnehmer können bei jeder Frage mehrere Antworten auswählen
                  </div>
                </div>
              </label>

              {/* Never Multiple */}
              <label className={`
                flex items-start p-3 border-2 rounded-lg cursor-pointer theme-transition
                ${currentSettings.multiAnswerMode === 'never'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }
              `}>
                <input
                  type="radio"
                  name="multiAnswerMode"
                  value="never"
                  checked={currentSettings.multiAnswerMode === 'never'}
                  onChange={(e) => updateSetting('multiAnswerMode', e.target.value)}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    Immer Single-Choice
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Nur eine Antwort pro Frage erlaubt (auch wenn mehrere richtig sind)
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </Card>

      {/* STUDENT REGISTRATION */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Studierenden-Registrierung
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Wie sollen Studierende am Quiz teilnehmen?
            </label>
            
            <div className="space-y-2">
              {/* Option: None */}
              <label className={`
                flex items-start p-3 border-2 rounded-lg cursor-pointer theme-transition
                ${currentSettings.studentRegistration === 'none'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }
              `}>
                <input
                  type="radio"
                  name="studentRegistration"
                  value="none"
                  checked={currentSettings.studentRegistration === 'none'}
                  onChange={(e) => updateSetting('studentRegistration', e.target.value)}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    Keine Registrierung
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Teilnehmer wählen nur einen Zufallsnamen oder "Anonym"
                  </div>
                </div>
              </label>

              {/* Option: Optional */}
              <label className={`
                flex items-start p-3 border-2 rounded-lg cursor-pointer theme-transition
                ${currentSettings.studentRegistration === 'optional'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }
              `}>
                <input
                  type="radio"
                  name="studentRegistration"
                  value="optional"
                  checked={currentSettings.studentRegistration === 'optional'}
                  onChange={(e) => updateSetting('studentRegistration', e.target.value)}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    Registrierung optional
                    <span className="ml-2 text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      Empfohlen
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Studierende können freiwillig ein Konto erstellen, um ihren Fortschritt zu verfolgen
                  </div>
                </div>
              </label>

              {/* Option: Required */}
              <label className={`
                flex items-start p-3 border-2 rounded-lg cursor-pointer theme-transition
                ${currentSettings.studentRegistration === 'required'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }
              `}>
                <input
                  type="radio"
                  name="studentRegistration"
                  value="required"
                  checked={currentSettings.studentRegistration === 'required'}
                  onChange={(e) => updateSetting('studentRegistration', e.target.value)}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    Registrierung verpflichtend
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Alle Teilnehmer müssen sich vor dem Quiz mit E-Mail registrieren
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Info Box */}
          {(currentSettings.studentRegistration === 'optional' || currentSettings.studentRegistration === 'required') && (
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  <p className="font-medium mb-1">Registrierte Studierende erhalten:</p>
                  <ul className="space-y-0.5 text-xs list-disc ml-4">
                    <li>Automatische Einschreibung in deinen Kurs</li>
                    <li>Fortschrittsverfolgung über alle Quiz</li>
                    <li>Zugriff auf ihr persönliches Dashboard</li>
                    <li>E-Mail-Verifizierung für Accountsicherheit</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* PRACTICE MODE */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          Übungsmodus
        </h3>

        <div className="space-y-4">
          <Toggle
            checked={currentSettings.allowPractice ?? true}
            onChange={(checked) => updateSetting('allowPractice', checked)}
            label="Quiz zum Üben freigeben"
          />
          
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Wenn aktiviert, können Studierende dieses Quiz jederzeit selbstständig üben. 
            Die Übungs-Ergebnisse werden separat von Live-Sessions gespeichert.
          </p>
        </div>
      </Card>

      {/* GAME FEATURES */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Gamification</h3>
          
          {/* Schnellvorlagen Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                onSettingsChange({
                  ...currentSettings,
                  enableGamification: false,
                  enableQuestionTimer: false,
                  enableJokers: false,
                  showLeaderboard: false,
                  showStreak: false,
                });
              }}
              className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30 theme-transition"
              title="Basis-Quiz, keine Gamification"
            >
              Einfach
            </button>
            <button
              onClick={() => {
                onSettingsChange({
                  ...currentSettings,
                  enableGamification: true,
                  enableQuestionTimer: false,
                  enableJokers: false,
                  showLeaderboard: true,
                  showStreak: true,
                  enableTimeBonus: false,
                });
              }}
              className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-success-50 dark:hover:bg-success-900/30 theme-transition"
              title="Punkte & Bestenliste"
            >
              Wettbewerb
            </button>
            <button
              onClick={() => {
                onSettingsChange({
                  ...currentSettings,
                  enableGamification: true,
                  enableQuestionTimer: true,
                  questionTimerSeconds: 30,
                  enableJokers: true,
                  showLeaderboard: true,
                  showStreak: true,
                  enableTimeBonus: true,
                  enableJoker5050: true,
                  enableJokerAudience: true,
                  enableJokerBestPlayer: true,
                });
              }}
              className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-900/30 theme-transition"
              title="Alle Features aktiviert"
            >
              Spielshow
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <Toggle
            checked={currentSettings.enableGamification}
            onChange={(checked) => updateSetting('enableGamification', checked)}
            label="Gamification aktivieren (Punkte, Bestenliste)"
          />

          {currentSettings.enableGamification && (
            <div className="pl-6 space-y-4 border-l-2 border-primary-200 dark:border-primary-700">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Basispunkte pro Frage
                </label>
                <input
                  type="number"
                  value={currentSettings.basePointsPerQuestion}
                  onChange={(e) => updateSetting('basePointsPerQuestion', parseInt(e.target.value) || 100)}
                  className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  min="1"
                  step="10"
                />
              </div>

              {/* Schalter im Grid-Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <Toggle
                  checked={currentSettings.enableTimeBonus}
                  onChange={(checked) => updateSetting('enableTimeBonus', checked)}
                  label="Zeitbonus"
                />

                <Toggle
                  checked={currentSettings.showLeaderboard}
                  onChange={(checked) => updateSetting('showLeaderboard', checked)}
                  label="Bestenliste"
                />

                <Toggle
                  checked={currentSettings.showStreak}
                  onChange={(checked) => updateSetting('showStreak', checked)}
                  label="Streak-Zähler"
                />

                <Toggle
                  checked={currentSettings.enableStreakBonus}
                  onChange={(checked) => updateSetting('enableStreakBonus', checked)}
                  label="Streak-Bonus"
                />
              </div>

              {currentSettings.enableStreakBonus && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bonuspunkte pro Streak
                  </label>
                  <input
                    type="number"
                    value={currentSettings.streakBonusPoints}
                    onChange={(e) => updateSetting('streakBonusPoints', parseInt(e.target.value) || 50)}
                    className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    min="0"
                    step="10"
                  />
                </div>
              )}

              {/* Joker-Bereich */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <Toggle
                  checked={currentSettings.enableJokers}
                  onChange={(checked) => updateSetting('enableJokers', checked)}
                  label="🃏 Joker aktivieren"
                />

                {currentSettings.enableJokers && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Toggle
                      checked={currentSettings.enableJoker5050}
                      onChange={(checked) => updateSetting('enableJoker5050', checked)}
                      label="50:50"
                    />

                    <Toggle
                      checked={currentSettings.enableJokerAudience}
                      onChange={(checked) => updateSetting('enableJokerAudience', checked)}
                      label="Publikum"
                    />

                    <Toggle
                      checked={currentSettings.enableJokerBestPlayer}
                      onChange={(checked) => updateSetting('enableJokerBestPlayer', checked)}
                      label="Bester Spieler"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* TIMER SETTINGS */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Timer</h3>

        <div className="space-y-4">
          <Toggle
            checked={currentSettings.enableQuestionTimer}
            onChange={(checked) => updateSetting('enableQuestionTimer', checked)}
            label="Fragen-Timer aktivieren"
          />

          {currentSettings.enableQuestionTimer && (
            <div className="pl-6 border-l-2 border-warning-200 dark:border-warning-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sekunden pro Frage
              </label>
              <input
                type="number"
                value={currentSettings.questionTimerSeconds}
                onChange={(e) => updateSetting('questionTimerSeconds', parseInt(e.target.value) || 30)}
                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-warning-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                min="5"
                max="300"
                step="5"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Teilnehmer müssen innerhalb dieser Zeit antworten
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* ACCESS CONTROL - Hidden: always enabled by default */}

      {/* DANGER ZONE */}
      <Card>
        <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4 flex items-center gap-2">
          ⚠️ Gefahrenbereich
        </h3>
        
        <div className="space-y-4">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg">
            <h4 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">
              Template löschen
            </h4>
            <p className="text-xs text-red-700 dark:text-red-400 mb-3">
              Diese Aktion kann nicht rückgängig gemacht werden. Das Template wird dauerhaft gelöscht.
            </p>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Möchten Sie dieses Template wirklich unwiderruflich löschen?')) {
                  // This will be handled by the parent component (TemplateEditorView)
                  // by listening for this event
                  window.dispatchEvent(new CustomEvent('deleteTemplate'));
                }
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg theme-transition-fast"
            >
              🗑️ Template endgültig löschen
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default SettingsTab;
